import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, isValid as isValidDate, parseISO } from "date-fns";
import {
  Calendar,
  ChevronDown,
  Search,
  SlidersHorizontal,
  Square,
  SquareCheck,
  X,
} from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Select } from "../ds";
import { statusColor, StatusDot, type CategoryColors } from "./StatusChip";
import { useDepartmentNames } from "../hooks/useDepartments";
import { displayDepartment, type Department } from "../api/departments";
import {
  buildUnitFilterOptions,
  COMMON_UNIT_LABEL,
  hasMultipleUnitGroups,
  sortDepartmentsByUnit,
  unitGroupLabel,
} from "../utils/departments";
import { periodToDates } from "../utils/period";
import {
  findFacet,
  statusLabel,
  useWorkflowConfig,
} from "../hooks/useWorkflow";
import {
  selectedStatusGroups,
  STATUS_FILTER_FACET,
  statusesForGroups,
  statusGroupsFromFacet,
} from "../utils/statusGroups";
import {
  CATEGORIES,
  REWARD_CATEGORIES,
  type SuggestionListFilters,
  type SuggestionStatus,
} from "../types/ess";

interface Props {
  value: SuggestionListFilters;
  onChange: (next: SuggestionListFilters) => void;
  /**
   * Extra department names to merge in (e.g. observed from the current
   * results). The canonical list is fetched from /departments/.
   */
  departments?: string[];
  /**
   * Extra category values to merge in (e.g. observed on rows in the
   * current results). Keeps legacy suggestions filterable even though
   * their category predates the current `CATEGORIES` picker list.
   */
  categories?: string[];
  /**
   * Optional trailing slot rendered inline with the filter controls.
   */
  trailing?: ReactNode;
  /**
   * Optional count of matching items. When provided and any filter is
   * active, shown as a "Showing N suggestion(s)" prefix above the active
   * chips row.
   */
  matchCount?: number;
  /**
   * When true, every filter control becomes read-only. Used on drill-down
   * pages where the filter set is preselected by the originating tile and
   * editing here would silently break the drill-down semantics. Active
   * chips render without a delete affordance and "Clear all" is hidden.
   */
  disabled?: boolean;
  /**
   * Optional client-side search field. When provided, renders an inline
   * search input at the front of the filter row. Owner is responsible for
   * applying the filter against the rendered list; we just collect the
   * keystrokes.
   */
  search?: string;
  onSearchChange?: (next: string) => void;
  /**
   * When true, the filter dropdowns are expanded on mount.
   */
  defaultOpen?: boolean;
  /**
   * Replaces the status chips with one chip — a Pipeline drill-down's
   * reachable set reads as a jumble of groups and states otherwise. Its
   * delete stays live under `disabled`, and should leave the drill-down.
   */
  statusSummary?: { label: string; onClear: () => void };
}

// Period range math (today/this_month/this_quarter/... -> date_from/
// date_to) lives in `../utils/period` (`periodToDates`) so it's unit
// testable independent of this component — see `period.test.ts`.
const PERIOD_OPTIONS = [
  { label: "All Time", value: "" },
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "Last 90 Days", value: "90d" },
  { label: "This Month", value: "this_month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "Last Quarter", value: "last_quarter" },
  { label: "This Year", value: "this_year" },
  { label: "Custom", value: "custom" },
];

const FILTER_SX = {
  width: "100%",
  minWidth: 0,
  // The MultiSelect (Autocomplete) input defaults to cursor: text. Force
  // pointer across the whole control so it reads as clickable like the
  // Period Select next to it.
  cursor: "pointer",
  "& .MuiInputBase-root, & .MuiInputBase-input, & input": {
    cursor: "pointer",
  },
  "& .MuiInputBase-input": { minWidth: 0 },
  "& input": { minWidth: 0, width: "100%" },
} as const;

const checkboxIcon = <RavenIcon icon={Square} sx={{ fontSize: 18 }} />;
const checkboxCheckedIcon = (
  <RavenIcon icon={SquareCheck} sx={{ fontSize: 18 }} />
);

// Lucide calendar glyph for the custom-range From/To DatePickers' open
// button — x-date-pickers defaults to its own calendar glyph. Static;
// color inherits from the picker's IconButton via currentColor.
const CalendarPickerIcon = () => (
  <RavenIcon icon={Calendar} sx={{ fontSize: 18 }} />
);

// Sentinel value for the "All" row rendered at the top of every multi-select.
// Kept in-module so it never collides with a real option value.
const ALL_SENTINEL = "__all__";

interface MultiProps<T extends string> {
  label: string;
  placeholder: string;
  options: T[];
  value: T[];
  getOptionLabel?: (opt: T) => string;
  // An optional dot rendered before the row label. Used by the Status
  // filter to mirror the StatusChip dot (colour, and ring for TEAL).
  getOptionDot?: (opt: T) => Pick<CategoryColors, "dot" | "ring"> | undefined;
  // Groups options under sticky subheaders (e.g. departments by unit). Options
  // must be pre-sorted by group; the "All" sentinel renders headerless on top.
  groupBy?: (opt: T) => string;
  onChange: (next: T[]) => void;
  // How many filter values are active, when that differs from the number of
  // checked options (the Status filter's raw-state leftovers).
  activeCount?: number;
  // The field's clear button. Defaults to `onChange([])`.
  onClear?: () => void;
  disabled?: boolean;
}

function MultiSelect<T extends string>({
  label,
  placeholder,
  options,
  value,
  getOptionLabel,
  getOptionDot,
  groupBy,
  onChange,
  activeCount,
  onClear,
  disabled,
}: MultiProps<T>) {
  const theme = useTheme();
  const allOption = ALL_SENTINEL as unknown as T;
  const optionsWithAll = [allOption, ...options];
  const allSelected = options.length > 0 && value.length === options.length;
  const someSelected = value.length > 0 && !allSelected;
  // Keep the "All" sentinel out of any unit group so it stays headerless on top.
  const groupByWithAll = groupBy
    ? (o: T) =>
        (o as unknown as string) === ALL_SENTINEL ? "" : groupBy(o)
    : undefined;

  // MUI only draws its clear button while the Autocomplete value is non-empty.
  // A Status filter holding only raw-state leftovers (an old
  // `?status=implemented` link) has an empty value but an active filter, so
  // it gets a clear button of our own, styled as MUI's.
  const showLeftoverClear =
    !disabled && value.length === 0 && (activeCount ?? 0) > 0 && !!onClear;

  const labelOf = (o: T) =>
    (o as unknown as string) === ALL_SENTINEL
      ? "All"
      : getOptionLabel
        ? getOptionLabel(o)
        : String(o);

  return (
    <Autocomplete<T, true, false, false>
      multiple
      disableCloseOnSelect
      size="small"
      disabled={disabled}
      options={optionsWithAll}
      value={value}
      onChange={(_e, next, reason) => {
        if (reason === "clear" && onClear) {
          onClear();
          return;
        }
        const toggled = (next as T[]).includes(allOption);
        if (toggled) {
          onChange(allSelected ? [] : options);
          return;
        }
        onChange(next as T[]);
      }}
      getOptionLabel={labelOf}
      groupBy={groupByWithAll}
      renderGroup={
        groupByWithAll
          ? (params) => (
              <li key={params.key}>
                {params.group ? (
                  <Box
                    sx={{
                      px: 2,
                      py: 0.5,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: "text.secondary",
                      lineHeight: "28px",
                      position: "sticky",
                      top: -4,
                      bgcolor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    {params.group}
                  </Box>
                ) : null}
                <Box component="ul" sx={{ p: 0 }}>
                  {params.children}
                </Box>
              </li>
            )
          : undefined
      }
      sx={FILTER_SX}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props as typeof props & { key: string };
        const isAll = (option as unknown as string) === ALL_SENTINEL;
        const checked = isAll ? allSelected : selected;
        const indeterminate = isAll && someSelected;
        const dot = !isAll && getOptionDot ? getOptionDot(option) : undefined;
        return (
          <li
            key={key}
            {...optionProps}
            style={{
              whiteSpace: "nowrap",
              ...(optionProps.style ?? {}),
            }}
          >
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkboxCheckedIcon}
              size="small"
              sx={{ mr: 1, p: 0.25 }}
              checked={checked}
              indeterminate={indeterminate}
            />
            {dot && (
              <Box component="span" sx={{ mr: 1, display: "inline-flex" }}>
                <StatusDot color={dot.dot} ring={dot.ring} />
              </Box>
            )}
            {isAll ? "All" : getOptionLabel ? getOptionLabel(option) : String(option)}
          </li>
        );
      }}
      renderTags={() => null}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={
            showLeftoverClear
              ? {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <IconButton
                        size="small"
                        aria-label={`Clear ${label}`}
                        title="Clear"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClear?.();
                        }}
                        sx={{ p: "4px", mr: "2px", visibility: "visible" }}
                      >
                        <RavenIcon icon={X} sx={{ fontSize: 18 }} />
                      </IconButton>
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }
              : params.InputProps
          }
          label={label}
          placeholder={
            (activeCount ?? value.length) === 0
              ? placeholder
              : `${activeCount ?? value.length} selected`
          }
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{
            "& input::placeholder": {
              color: "text.primary",
              opacity: 1,
            },
          }}
        />
      )}
      slotProps={{
        popper: {
          sx: {
            width: "auto !important",
            minWidth: 0,
            "& .MuiAutocomplete-paper": {
              width: "max-content",
              minWidth: "100%",
              maxWidth: "min(440px, calc(100vw - 32px))",
              borderRadius: "12px",
              boxShadow:
                "rgba(0,0,0,0.2) 0px 2px 4px -1px, rgba(0,0,0,0.14) 0px 4px 5px 0px, rgba(0,0,0,0.12) 0px 1px 10px 0px",
              // Match MenuItem's visual rhythm used by the Period <Select>.
              "& .MuiAutocomplete-listbox": {
                padding: "4px 0",
                // MUI's 40vh clips the Status list (All + 8 groups) on a
                // 900px-tall viewport.
                maxHeight: "min(60vh, 480px)",
              },
              "& .MuiAutocomplete-option": {
                minHeight: 36,
                paddingTop: "6px",
                paddingBottom: "6px",
              },
              // Soft copper tint for the currently-selected row, matching
              // Period's Mui-selected style.
              "& .MuiAutocomplete-option[aria-selected='true']": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
            },
          },
          modifiers: [{ name: "flip", enabled: true }],
          placement: "bottom-start",
        },
      }}
    />
  );
}

export default function SuggestionFilters({
  value,
  onChange,
  departments = [],
  categories = [],
  trailing,
  matchCount,
  disabled = false,
  search,
  onSearchChange,
  defaultOpen = false,
  statusSummary,
}: Props) {
  const { names: fetched, departments: catalog } = useDepartmentNames();
  const { data: workflow } = useWorkflowConfig();

  const deptByName = useMemo(() => {
    const m = new Map<string, Department>();
    catalog.forEach((d) => {
      if (d?.name) m.set(d.name, d);
    });
    return m;
  }, [catalog]);

  // Department names to offer: those known to the API plus any the parent
  // passes in, resolved to catalog entries so we can group them by unit.
  const departmentDeptList = useMemo(() => {
    const names = Array.from(new Set<string>([...fetched, ...departments]));
    return names.map((n) => deptByName.get(n) ?? ({ name: n } as Department));
  }, [fetched, departments, deptByName]);

  const showDepartmentGroups = useMemo(
    () => hasMultipleUnitGroups(departmentDeptList),
    [departmentDeptList],
  );

  const departmentOptions = useMemo(
    () =>
      showDepartmentGroups
        ? sortDepartmentsByUnit(departmentDeptList).map((d) => d.name)
        : departmentDeptList.map((d) => d.name).sort(),
    [departmentDeptList, showDepartmentGroups],
  );

  const departmentGroupBy = useMemo(
    () =>
      showDepartmentGroups
        ? (name: string) => unitGroupLabel(deptByName.get(name) ?? {})
        : undefined,
    [showDepartmentGroups, deptByName],
  );

  const statusOptions = useMemo(
    () => Object.keys(workflow?.states ?? {}) as SuggestionStatus[],
    [workflow],
  );
  const statusLabelOf = (s: SuggestionStatus) => statusLabel(workflow, s);

  // The Status filter offers the `all_suggestions` facet's groups
  // (With Custodian, Rejected, …) rather than every raw state. The filter
  // value itself stays a list of raw states — that is what the list API,
  // the URL and the Overview drill-downs speak — and a group maps to the
  // union of its states. A raw state that completes no group (an older
  // `?status=implemented` link) keeps filtering on its own and gets its own
  // chip. `null` = the backend sent no `states`, so fall back to raw states.
  const statusGroups = useMemo(
    () =>
      statusGroupsFromFacet(findFacet(workflow?.facets, STATUS_FILTER_FACET)),
    [workflow],
  );
  const statusGroupSelection = useMemo(
    () =>
      statusGroups
        ? selectedStatusGroups(statusGroups, value.status ?? [])
        : null,
    [statusGroups, value.status],
  );

  // Legacy rows may carry a category from before the current CATEGORIES
  // list — union them in so those suggestions stay filterable even though
  // the value no longer appears in the canonical picker list.
  const categoryOptions = useMemo(
    () => Array.from(new Set<string>([...CATEGORIES, ...categories])),
    [categories],
  );

  // Unit filter options via `buildUnitFilterOptions` (utils/departments,
  // unit-tested): distinct real units sorted, "Common" last — and only
  // when the catalog actually contains a common/unit-less department.
  // `metadata.unit` on rows is always a real unit or absent (never the
  // literal "Common"), so an unconditional "Common" option would silently
  // match zero rows on an all-real-units account. On an account with no
  // real units the control hides entirely (same spirit as the department
  // dropdown dropping its group headers when there's only one group) — a
  // Unit filter offering only "Common" matches every row and is pure
  // noise. Active `unit` chips still render regardless, so a drill-down
  // URL param stays visible/clearable either way.
  const unitOptions = useMemo(() => buildUnitFilterOptions(catalog), [catalog]);
  const hasRealUnits = unitOptions.some((u) => u !== COMMON_UNIT_LABEL);

  // A–F reward band, computed by the SC evaluation (rewardCategoryFor).
  const rewardOptions = useMemo(() => [...REWARD_CATEGORIES], []);

  const [period, setPeriod] = useState("");
  const [mobileOpen, setMobileOpen] = useState(defaultOpen);
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);
  const [fromOpen, setFromOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const ymd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const activeCount =
    (period ? 1 : 0) +
    (value.status && value.status.length > 0 ? 1 : 0) +
    (value.category && value.category.length > 0 ? 1 : 0) +
    (value.department && value.department.length > 0 ? 1 : 0) +
    (value.unit && value.unit.length > 0 ? 1 : 0) +
    (value.reward_category && value.reward_category.length > 0 ? 1 : 0);

  const periodLabel = (() => {
    if (period !== "custom") {
      return PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? "";
    }
    // Prefer the applied value (date_from/date_to from URL or parent
    // state) over the local picker state — on a fresh mount with URL
    // params the local pickers are still null but a range is active.
    // parseISO treats "YYYY-MM-DD" as local midnight; `new Date(ymd)`
    // would treat it as UTC midnight and render one day early in
    // negative-UTC timezones.
    const formatYmd = (ymd: string): string => {
      const d = parseISO(ymd);
      return isValidDate(d) ? format(d, "dd MMM yyyy") : ymd;
    };
    if (value.date_from && value.date_to) {
      return `${formatYmd(value.date_from)} - ${formatYmd(value.date_to)}`;
    }
    if (value.date_from) return `From ${formatYmd(value.date_from)}`;
    if (value.date_to) return `Until ${formatYmd(value.date_to)}`;
    return "Custom";
  })();

  interface ActiveChip {
    key: string;
    label: string;
    onDelete: () => void;
    // Deletable even when the row is `disabled`.
    alwaysDeletable?: boolean;
  }
  const activeChips: ActiveChip[] = [];
  if (period) {
    activeChips.push({
      key: `period:${period}`,
      label: periodLabel,
      onDelete: () => {
        setPeriod("");
        setCustomFrom(null);
        setCustomTo(null);
        onChange({ ...value, date_from: undefined, date_to: undefined });
      },
    });
  }
  const statusChips = statusSummary && (value.status ?? []).length > 0;
  if (statusChips) {
    activeChips.push({
      key: "status-summary",
      label: statusSummary.label,
      onDelete: statusSummary.onClear,
      alwaysDeletable: true,
    });
  }
  (statusChips ? [] : statusGroupSelection?.selected ?? []).forEach((g) =>
    activeChips.push({
      key: `status-group:${g.name}`,
      label: g.label,
      onDelete: () => {
        const drop = new Set(g.states);
        const next = (value.status ?? []).filter((x) => !drop.has(x));
        onChange({ ...value, status: next.length ? next : undefined });
      },
    }),
  );
  (statusChips
    ? []
    : statusGroupSelection?.leftovers ?? value.status ?? []
  ).forEach((s) =>
    activeChips.push({
      key: `status:${s}`,
      label: statusLabel(workflow, s),
      onDelete: () => {
        const next = (value.status ?? []).filter((x) => x !== s);
        onChange({ ...value, status: next.length ? next : undefined });
      },
    }),
  );
  (value.category ?? []).forEach((c) =>
    activeChips.push({
      key: `category:${c}`,
      label: c,
      onDelete: () => {
        const next = (value.category ?? []).filter((x) => x !== c);
        onChange({ ...value, category: next.length ? next : undefined });
      },
    }),
  );
  (value.department ?? []).forEach((d) =>
    activeChips.push({
      key: `department:${d}`,
      label: displayDepartment(d),
      onDelete: () => {
        const next = (value.department ?? []).filter((x) => x !== d);
        onChange({ ...value, department: next.length ? next : undefined });
      },
    }),
  );
  (value.unit ?? []).forEach((u) =>
    activeChips.push({
      key: `unit:${u}`,
      label: u,
      onDelete: () => {
        const next = (value.unit ?? []).filter((x) => x !== u);
        onChange({ ...value, unit: next.length ? next : undefined });
      },
    }),
  );
  (value.reward_category ?? []).forEach((r) =>
    activeChips.push({
      key: `reward:${r}`,
      label: `Reward ${r}`,
      onDelete: () => {
        const next = (value.reward_category ?? []).filter((x) => x !== r);
        onChange({
          ...value,
          reward_category: next.length ? next : undefined,
        });
      },
    }),
  );

  const clearAll = () => {
    setPeriod("");
    setCustomFrom(null);
    setCustomTo(null);
    onChange({});
  };

  const activeChipsRow = activeChips.length > 0 && (
    <Box sx={{ mt: 1.5 }}>
      {/* Header line: "Applied filters" label, optional match count.
        Sits above the chip stack so screen readers and sighted users
        both have a clear "this is the filter context" anchor. */}
      <Stack
        direction="row"
        alignItems="baseline"
        spacing={1}
        sx={{ mb: 0.75 }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ lineHeight: 1, letterSpacing: "0.08em", fontWeight: 600 }}
        >
          Applied Filters
        </Typography>
        {matchCount !== undefined && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              aria-hidden="true"
            >
              ·
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Showing{" "}
              <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
                {matchCount.toLocaleString()}
              </Box>{" "}
              {matchCount === 1 ? "suggestion" : "suggestions"}
            </Typography>
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        spacing={0.75}
      >
        {activeChips.map((c) => (
          <Chip
            key={c.key}
            label={c.label}
            size="small"
            onDelete={
              disabled && !c.alwaysDeletable ? undefined : c.onDelete
            }
            sx={(theme) => ({
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              "& .MuiChip-deleteIcon": {
                color: theme.palette.text.secondary,
                "&:hover": { color: theme.palette.text.primary },
              },
            })}
          />
        ))}
        {!disabled && activeChips.length > 1 && (
          <Button
            size="small"
            onClick={clearAll}
            sx={{
              ml: 0.5,
              // Inherits product button-medium typography from the theme;
              // only the borderRadius + color customization stays local.
              borderRadius: 999,
              color: "text.secondary",
            }}
          >
            Clear all
          </Button>
        )}
      </Stack>
    </Box>
  );

  // Filter pills sit in their own row; `trailing` and `searchField` are
  // rendered separately in the row above.
  const columns = 4;
  const twoColTemplate = "repeat(2, minmax(0, 1fr))";

  const searchField = onSearchChange ? (
    <TextField
      size="small"
      placeholder="Search by title, number, or employee…"
      value={search ?? ""}
      onChange={(e) => onSearchChange(e.target.value)}
      fullWidth
      hiddenLabel
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <RavenIcon
                icon={Search}
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => onSearchChange("")}
                edge="end"
              >
                <RavenIcon icon={X} sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          py: 0,
          minHeight: 40,
        },
      }}
    />
  ) : null;

  const handleCustomDate = (from: Date | null, to: Date | null) => {
    setCustomFrom(from);
    setCustomTo(to);
    if (from && to && isValidDate(from) && isValidDate(to)) {
      onChange({
        ...value,
        date_from: ymd(from),
        date_to: ymd(to),
      });
    }
  };

  // Auto-collapse the date row once a full custom range is applied —
  // the Applied Filters chip below carries the same information, so the
  // pickers only need to be visible while the user is still editing.
  // To change the range, the user clears the chip and picks again.
  const customRangeApplied = Boolean(value.date_from && value.date_to);
  const customDateRow = period === "custom" && !customRangeApplied && (
    <Stack
      direction="row"
      spacing={1.5}
      // At lg (4-col grid) the date row occupies only the first two
      // tracks so each date picker matches Period/Status field width.
      // Smaller breakpoints keep the full-row span — the grid is narrower
      // so the fields don't blow out.
      sx={{ gridColumn: { xs: "1 / -1", lg: "1 / span 2" } }}
    >
      <DatePicker
        label="From"
        value={customFrom}
        open={fromOpen}
        onOpen={() => setFromOpen(true)}
        onClose={() => setFromOpen(false)}
        onChange={(d) => handleCustomDate(d, customTo)}
        maxDate={customTo ?? undefined}
        disabled={disabled}
        format="dd/MM/yyyy"
        slots={{ openPickerIcon: CalendarPickerIcon }}
        slotProps={{
          textField: { size: "small", fullWidth: true, sx: FILTER_SX },
        }}
      />
      <DatePicker
        label="To"
        value={customTo}
        onChange={(d) => handleCustomDate(customFrom, d)}
        minDate={customFrom ?? undefined}
        disabled={disabled}
        format="dd/MM/yyyy"
        slots={{ openPickerIcon: CalendarPickerIcon }}
        slotProps={{
          textField: { size: "small", fullWidth: true, sx: FILTER_SX },
        }}
      />
    </Stack>
  );

  const filterFields = (
    <>
      <Select
        label="Period"
        value={period}
        onChange={(v) => {
          setPeriod(v);
          if (v === "custom") {
            // Reset custom dates — user will pick via the date pickers
            setCustomFrom(null);
            setCustomTo(null);
            // Auto-open the From date picker so the user can start picking immediately
            setTimeout(() => setFromOpen(true), 0);
            onChange({ ...value, date_from: undefined, date_to: undefined });
          } else {
            setCustomFrom(null);
            setCustomTo(null);
            const dates = periodToDates(v);
            onChange({
              ...value,
              date_from: dates.date_from,
              date_to: dates.date_to,
            });
          }
        }}
        options={PERIOD_OPTIONS}
        fullWidth
        disabled={disabled}
        sx={FILTER_SX}
      />
      {statusGroups && statusGroupSelection ? (
        <MultiSelect
          label="Status"
          placeholder="All Statuses"
          options={statusGroups.map((g) => g.name)}
          value={statusGroupSelection.selected.map((g) => g.name)}
          getOptionLabel={(name) =>
            statusGroups.find((g) => g.name === name)?.label ?? name
          }
          getOptionDot={(name) => {
            const group = statusGroups.find((g) => g.name === name);
            return group
              ? statusColor(workflow, group.states[0], theme.palette.mode)
              : undefined;
          }}
          onChange={(names) => {
            const picked = statusGroups.filter((g) => names.includes(g.name));
            const next = statusesForGroups(
              picked,
              statusGroupSelection.leftovers,
            );
            onChange({ ...value, status: next.length ? next : undefined });
          }}
          activeCount={
            statusGroupSelection.selected.length +
            statusGroupSelection.leftovers.length
          }
          onClear={() => onChange({ ...value, status: undefined })}
          disabled={disabled}
        />
      ) : (
        <MultiSelect
          label="Status"
          placeholder="All Statuses"
          options={statusOptions}
          value={value.status ?? []}
          getOptionLabel={statusLabelOf}
          getOptionDot={(s) => statusColor(workflow, s, theme.palette.mode)}
          onChange={(v) =>
            onChange({
              ...value,
              status: v.length ? v : undefined,
            })
          }
          disabled={disabled}
        />
      )}
      <MultiSelect
        label="Category"
        placeholder="All Categories"
        options={categoryOptions}
        value={value.category ?? []}
        onChange={(v) =>
          onChange({ ...value, category: v.length ? v : undefined })
        }
        disabled={disabled}
      />
      <MultiSelect
        label="Department"
        placeholder="All Departments"
        options={departmentOptions}
        getOptionLabel={(d) => displayDepartment(d)}
        groupBy={departmentGroupBy}
        value={value.department ?? []}
        onChange={(v) =>
          onChange({ ...value, department: v.length ? v : undefined })
        }
        disabled={disabled}
      />
      {hasRealUnits && (
        <MultiSelect
          label="Unit"
          placeholder="All Units"
          options={unitOptions}
          value={value.unit ?? []}
          onChange={(v) =>
            onChange({ ...value, unit: v.length ? v : undefined })
          }
          disabled={disabled}
        />
      )}
      <MultiSelect
        label="Reward Category"
        placeholder="All Reward Categories"
        options={rewardOptions}
        value={value.reward_category ?? []}
        onChange={(v) =>
          onChange({
            ...value,
            reward_category: v.length ? v : undefined,
          })
        }
        disabled={disabled}
      />
      {customDateRow}
    </>
  );

  if (isMobile) {
    return (
      <Box
        data-tour="suggestions-filters"
        sx={{ mb: 2, pt: 1, pb: 0.5, width: "100%" }}
      >
        {searchField && <Box sx={{ mb: 1 }}>{searchField}</Box>}
        {/* The toggles keep single-line labels; at 390px they do not fit
            beside Filters, so the row wraps rather than squeezing them. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
        >
          <Button
            ref={triggerRef}
            onClick={() => setMobileOpen((o) => !o)}
            disabled={disabled}
            startIcon={
              <Badge
                badgeContent={activeCount}
                color="primary"
                invisible={activeCount === 0}
              >
                <RavenIcon icon={SlidersHorizontal} />
              </Badge>
            }
            endIcon={
              <RavenIcon
                icon={ChevronDown}
                style={{
                  transition: "transform .2s ease",
                  transform: mobileOpen ? "rotate(180deg)" : "none",
                }}
              />
            }
            variant="outlined"
            color="inherit"
            sx={(theme) => ({
              borderColor: mobileOpen
                ? theme.palette.primary.main
                : theme.palette.divider,
              color: mobileOpen
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
              bgcolor: mobileOpen
                ? alpha(theme.palette.primary.main, 0.08)
                : "transparent",
              textTransform: "none",
              letterSpacing: 0,
              fontWeight: 500,
              px: 1.5,
              "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                color: mobileOpen
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              },
              "&:hover": {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                  color: theme.palette.primary.main,
                },
              },
            })}
          >
            Filters
          </Button>
          {trailing && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {trailing}
            </Box>
          )}
        </Stack>
        <Popover
          open={mobileOpen}
          anchorEl={triggerRef.current}
          onClose={() => setMobileOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                p: 2,
                borderRadius: "12px",
                width: "min(320px, calc(100vw - 32px))",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              rowGap: 2,
            }}
          >
            {filterFields}
          </Box>
        </Popover>
        {activeChipsRow}
      </Box>
    );
  }

  // Drilldown / locked context — every interactive control is read-only
  // anyway, so collapse the chrome down to just the active filter chips.
  // The chips already communicate what's filtered without the visual
  // weight of four greyed-out dropdowns + a disabled search field.
  if (disabled) {
    return (
      <Box sx={{ width: "100%", pt: 1, pb: 0.5, mb: 2 }}>
        {activeChipsRow}
      </Box>
    );
  }

  return (
    <Box data-tour="suggestions-filters" sx={{ width: "100%", pt: 1, pb: 0.5, mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          ref={triggerRef}
          onClick={() => setMobileOpen((o) => !o)}
          disabled={disabled}
          startIcon={
            <Badge
              badgeContent={activeCount}
              color="primary"
              invisible={activeCount === 0}
            >
              <RavenIcon icon={SlidersHorizontal} />
            </Badge>
          }
          endIcon={
            <RavenIcon
              icon={ChevronDown}
              style={{
                transition: "transform .2s ease",
                transform: mobileOpen ? "rotate(180deg)" : "none",
              }}
            />
          }
          variant="outlined"
          color="inherit"
          sx={(theme) => ({
            borderColor: mobileOpen
              ? theme.palette.primary.main
              : theme.palette.divider,
            color: mobileOpen
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            bgcolor: mobileOpen
              ? alpha(theme.palette.primary.main, 0.08)
              : "transparent",
            textTransform: "none",
            letterSpacing: 0,
            fontWeight: 500,
            px: 1.5,
            flexShrink: 0,
            "& .MuiButton-startIcon, & .MuiButton-endIcon": {
              color: mobileOpen
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
            },
            "&:hover": {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                color: theme.palette.primary.main,
              },
            },
          })}
        >
          Filters
        </Button>
        {searchField && <Box sx={{ flex: 1 }}>{searchField}</Box>}
        {trailing && (
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {trailing}
          </Box>
        )}
      </Stack>
      <Collapse in={mobileOpen} timeout={200}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: twoColTemplate,
              md: twoColTemplate,
              lg: `repeat(${columns}, minmax(0, 1fr))`,
            },
            columnGap: 2,
            rowGap: 2,
            alignItems: "center",
            width: "100%",
            mt: 2,
          }}
        >
          {filterFields}
        </Box>
      </Collapse>
      {activeChipsRow}
    </Box>
  );
}
